import {
  inject,
  OnInit,
  signal,
  computed,
  ViewChild,
  Component,
  ElementRef,
  DestroyRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { CdkDragMove } from '@angular/cdk/drag-drop';

import { UserObjectsService } from 'src/app/core/services/user-objects.service';
import { WidgetService } from '../../../services/widget.service';

import { WidgetPreviewEvent } from '../../cards/widget-preset-card/widget-preset-card.component';

import {
  Feed,
  Widget,
  Language,
  ContentFilters,
  NomenclatureItem,
} from '../../../models/core.models';
import { WidgetPreset } from '../../../models/widget.models';

import { augmentObjects } from 'src/app/shared/utils/object-utils';

import { WidgetPersistenceService } from '../../../services/widget-persistence.service';

import {
  injectWidgetPresetGroupsQuery,
  injectWidgetPresetsQuery,
  WidgetPresetsScope,
} from 'src/app/api/queries/widget.queries';

import {
  getContentFilters,
  selectCoreLanguage,
  selectCurrentObject,
  selectSelectedPlaceholderId,
  selectCurrentObjectWorkspaceId,
} from '../../../reducers';
import { WorkspaceObjectsService } from '../../../services/workspace-objects.service';
import { AppState } from 'src/app/reducers';
import { NomObjectClass, ZIndex } from '../../../constants';

import {
  of,
  tap,
  map,
  take,
  filter,
  Subject,
  switchMap,
  Observable,
  debounceTime,
  combineLatest,
  BehaviorSubject,
  distinctUntilChanged,
} from 'rxjs';
import { ToolbarButton } from '../../../models/toolbar.models';
import { ComboboxOption } from '@sui/combobox';
import { withLatestFrom } from 'rxjs';
import { selectCurrentObjectCacheEnabled } from 'src/app/core/reducers';
import { selectWidgetsLanguageId } from 'src/app/core/reducers';

@Component({
  selector: 'app-widget-sidebar',
  templateUrl: './widget-sidebar.component.html',
  styleUrls: ['./widget-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WidgetSidebarComponent implements OnInit {
  @ViewChild('widgetInput') widgetInput: ElementRef;
  @ViewChild('sidebar', { static: true }) sidebarRef: ElementRef;

  // * Presets/groups come from Angular Query; the scope is set once the current
  // * object is known (see ngOnInit).
  private readonly presetsScope = signal<WidgetPresetsScope | null>(null);
  private readonly widgetPresetsQuery = injectWidgetPresetsQuery(
    this.presetsScope,
  );
  private readonly widgetPresetGroupsQuery = injectWidgetPresetGroupsQuery();

  widgetPresets$: Observable<WidgetPreset[]> = toObservable(
    computed(() => this.widgetPresetsQuery.data() ?? []),
  );
  widgetPresetsLoading$: Observable<boolean> = toObservable(
    computed(() => this.widgetPresetsQuery.isLoading()),
  );
  private readonly widgetPresetGroups$ = toObservable(
    computed(() => this.widgetPresetGroupsQuery.data() ?? []),
  );
  widgetPresetsFiltered: WidgetPreset[] = [];
  widgetPresets: WidgetPreset[] = [];
  groups: NomenclatureItem[] = [];
  // sui-combobox options for the group picker ({ title, id } → { label, value }).
  groupOptions: ComboboxOption[] = [];

  searchString: string = '';
  objectTypeId: number;
  objectClassId: number;
  objectId: string;
  currentGroupId: number = 0;
  contentFilters: ContentFilters;
  widgetPreviewEvent: WidgetPreviewEvent;
  selectedPresetGroupId: number = 0;
  selectedPlaceholderId: string = null;
  selectedPresetId: number = null;
  selectedChartTypeId: number = null;
  wideSidebar: boolean = true;

  searchSubject: Subject<string> = new Subject();
  feeds$: Observable<Feed[]>;
  // sui-combobox options for the feed picker. The value is the whole Feed object derived from the
  // same feeds$ stream so identities line up, with compareWith by id as a guard.
  feedOptions$: Observable<ComboboxOption<Feed>[]>;
  readonly compareFeed = (a: Feed, b: Feed): boolean => a?.id === b?.id;
  defaultPreviewFeed: Feed;
  nomObjectClass = NomObjectClass;

  // clear the sidebar buttons
  buttons$ = new BehaviorSubject<ToolbarButton[]>([]);

  language$: Observable<Language>;
  rtl$: Observable<boolean>;
  showPreviewSubject = new BehaviorSubject<boolean>(false);
  showPreview$ = this.showPreviewSubject.asObservable();

  // * docked state as observable for reactive positioning
  dockedSubject = new BehaviorSubject<boolean>(true);
  docked$ = this.dockedSubject.asObservable();

  enablePreview = true;
  previewWidget: Widget | null = null;
  previewIsLoading: boolean = false;
  currentPopupWidth: number = 800;
  currentPopupHeight: number = 400;
  rtl: boolean = false;

  // * dynamic positioning
  popupTop: number = 0;
  popupLeft: number = 0;

  // * docking state
  isDocked: boolean = true;

  // * drag position for CDK drag
  dragPosition = { x: 0, y: 0 };
  ZIndex = ZIndex;

  private destroyRef = inject(DestroyRef);
  private widgetPersistence = inject(WidgetPersistenceService);

  // new state
  pivotableOnly = false;

  constructor(
    private store: Store<AppState>,
    private widgetService: WidgetService,
    private userObjects: UserObjectsService,
    private cdr: ChangeDetectorRef,
    private workspaceObjects: WorkspaceObjectsService,
  ) {
    // * initialize docked state
    this.updateDockedState(true);

    this.store
      .select(selectCurrentObject)
      .pipe(
        tap((object) => {
          this.objectTypeId = object.object_type_id;
          this.objectClassId = object.object_class_id;
          this.objectId = object.id;

          this.presetsScope.set({
            objectTypeId: this.objectTypeId,
            objectClassId: this.objectClassId,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.language$ = this.store.select(selectCoreLanguage);
    this.rtl$ = this.language$.pipe(
      map((lang) => lang.rtl),
      tap((rtl) => {
        this.rtl = rtl;
      }),
    );

    if (this.objectClassId == this.nomObjectClass.DASHBOARD) {
      this.feeds$ = this.store.select(selectCurrentObjectWorkspaceId).pipe(
        switchMap((workspaceId) =>
          // * the workspace's active feeds, fetched only if the cache is empty
          this.workspaceObjects.activeFeeds$(workspaceId).pipe(
            switchMap((workspaceFeeds) => {
              if (workspaceFeeds.length > 0) {
                return of(workspaceFeeds);
              } else {
                // * fall back to all of the user's feeds
                return this.userObjects
                  .search$({
                    query: '',
                    workspaceId: '',
                    objectClassIds: [this.nomObjectClass.FEED],
                    objectTypeIds: [],
                  })
                  .pipe(
                    map(
                      (objects) =>
                        augmentObjects(
                          objects.filter(
                            (obj) =>
                              obj.object_class_id === this.nomObjectClass.FEED,
                          ),
                        ) as Feed[],
                    ),
                  );
              }
            }),
          ),
        ),
        tap((feeds) => {
          this.defaultPreviewFeed = feeds[0];
        }),
        takeUntilDestroyed(this.destroyRef),
      );

      this.feedOptions$ = this.feeds$.pipe(
        map((feeds) =>
          feeds.map((feed) => ({ label: feed.title, value: feed })),
        ),
      );
    }

    this.store
      .select(selectSelectedPlaceholderId)
      .pipe(
        tap((id) => {
          this.selectedPlaceholderId = id;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.widgetPresetGroups$
      .pipe(
        tap((groups) => {
          this.groups = [...groups];
          this.groups.unshift({ id: 0, title: 'All Widgets' });
          this.groupOptions = this.groups.map((group) => ({
            label: group.title,
            value: group.id,
          }));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(getContentFilters)
      .pipe(
        tap((contentFilters) => {
          this.contentFilters = contentFilters;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.searchSubject
      .pipe(
        debounceTime(300),
        tap((searchString) => {
          this.searchString = searchString;
          this.filterWidgets(searchString, this.pivotableOnly);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // * hide preview when RTL/LTR direction changes
    this.rtl$
      .pipe(
        distinctUntilChanged(),
        // * do it only if preview is currently shown
        filter(() => this.showPreviewSubject.value),
        tap((rtl) => {
          this.closeWidgetPreviewPopup();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // * handle initial positioning for docked popups
    combineLatest([this.rtl$, this.showPreview$])
      .pipe(
        filter(([_, show]) => show),
        tap(([rtl]) => {
          // ! only calculate position for initially docked popups
          if (this.isDocked) {
            this.calculatePopupPosition(rtl);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit() {
    this.widgetPresets$
      .pipe(
        tap((presets) => {
          this.widgetPresets = presets;
          this.widgetPresetsFiltered = presets;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  // * helper method to update docked state consistently
  private updateDockedState(docked: boolean) {
    this.isDocked = docked;
    this.dockedSubject.next(docked);
  }

  // * Handles the input change event.
  // * Updates the filtered widget presets based on the input value.
  handleInputChange(widgetSearchString: string) {
    this.searchSubject.next(widgetSearchString);
  }

  // * Filters the widget presets based on the provided search string and selected preset group.
  filterWidgets(widgetSearchString: string, pivotableOnly = false) {
    const searchString = widgetSearchString.toLowerCase();

    this.widgetPresetsFiltered = this.widgetPresets
      .map((preset) => {
        // if pivotableOnly = true, filter chart_types
        const chartTypes = pivotableOnly
          ? preset.chart_types.filter((viz) => viz.is_pivotable)
          : preset.chart_types;

        return {
          ...preset,
          chart_types: chartTypes,
        };
      })
      .filter((preset) => {
        // if pivotableOnly, skip presets with no pivotable viz left
        if (pivotableOnly && preset.chart_types.length === 0) {
          return false;
        }

        const matchesSearchString =
          searchString.length === 0 ||
          preset.name?.toLowerCase().includes(searchString) ||
          preset.description?.toLowerCase().includes(searchString);

        const matchesPresetGroup =
          this.selectedPresetGroupId === 0 ||
          preset.group_id === this.selectedPresetGroupId;

        return matchesSearchString && matchesPresetGroup;
      });

    this.clearSelection();
    this.cdr.detectChanges();
  }

  onOptionChange(selectElementValue: number) {
    const selectedValue = selectElementValue;
    this.selectedPresetGroupId = selectedValue;
    this.filterWidgets(this.searchString, this.pivotableOnly);
  }

  defaultPreviewFeedChange() {
    if (this.previewWidget && this.widgetPreviewEvent) {
      this.onWidgetPreview(this.widgetPreviewEvent);
    }
  }

  onWidgetPreview(event: WidgetPreviewEvent) {
    this.widgetPreviewEvent = event;

    // * update selection state
    this.selectedPresetId = event.widgetPreset.id;
    this.selectedChartTypeId = event.vizType.id;

    if (this.selectedPlaceholderId !== null) {
      this.createWidget();
      return;
    }

    // * show Preview popup
    this.showWidgetPreviewPopup(null, true);

    this.widgetService
      .getDefaultWidgetStructure(
        this.objectId,
        this.objectClassId,
        event.widgetPreset.id,
        event.vizType.id,
      )
      .pipe(
        // adjust structure + set previewWidget
        map((widgetStructure) => {
          let adjusted = widgetStructure;
          if (
            adjusted.datasources.length === 0 &&
            this.objectClassId === this.nomObjectClass.DASHBOARD
          ) {
            adjusted = this.handleDashboardWidgetPreview(adjusted);
          }
          this.previewWidget = adjusted;
          return adjusted;
        }),

        // pull cacheEnabled from the store
        withLatestFrom(
          this.store.select(selectWidgetsLanguageId),
          this.store.select(selectCurrentObjectCacheEnabled),
        ),

        // call preview with both widgetStructure + cacheEnabled
        switchMap(([widgetStructure, widgetsLanguageId, cacheEnabled]) =>
          this.widgetService.getWidgetPreview(
            widgetStructure,
            this.contentFilters,
            widgetsLanguageId,
            cacheEnabled,
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        (widgetPreview) => {
          this.previewWidget = {
            ...this.previewWidget,
            data: widgetPreview,
          };

          // * update Preview popup with widget data
          this.showWidgetPreviewPopup(this.previewWidget, false);
        },
        (error) => {
          console.log(error);
          this.showWidgetPreviewPopup(null, false);
        },
      );
  }

  handleDashboardWidgetPreview(widgetStructure: Widget): Widget {
    let updatedWidgetStructure: Widget = widgetStructure;

    updatedWidgetStructure.datasources = [this.defaultPreviewFeed];
    updatedWidgetStructure.settings = {
      ...updatedWidgetStructure.settings,
      datasource_object_id: [this.defaultPreviewFeed.id],
      datasource_object_class_id: [this.defaultPreviewFeed.object_class_id],
    };
    return updatedWidgetStructure;
  }

  onWidgetCreate(event: WidgetPreviewEvent) {}

  getDataSourceObjectId(): string {
    if (this.objectClassId === this.nomObjectClass.DASHBOARD) {
      return this.defaultPreviewFeed?.object_id;
    } else if (this.objectClassId === this.nomObjectClass.FEED) {
      return this.objectId;
    }
    return null;
  }

  getDataSourceObjectClassId(): number {
    if (this.objectClassId === this.nomObjectClass.DASHBOARD) {
      return this.defaultPreviewFeed?.object_class_id;
    } else if (this.objectClassId === this.nomObjectClass.FEED) {
      return this.objectClassId;
    }
    return null;
  }

  createWidget() {
    let dataSourceObjectId: string | undefined;
    let dataSourceObjectClassId: number | undefined;

    if (this.objectClassId === this.nomObjectClass.DASHBOARD) {
      dataSourceObjectId = this.defaultPreviewFeed?.object_id;
      dataSourceObjectClassId = this.defaultPreviewFeed?.object_class_id;
    } else if (this.objectClassId === this.nomObjectClass.FEED) {
      dataSourceObjectId = this.objectId;
      dataSourceObjectClassId = this.objectClassId;
    } else {
      return; // Do nothing for other object classes
    }

    // * the service resolves the clicked placeholder and its row/geometry itself
    this.widgetPersistence.createWidget({
      chartId: this.widgetPreviewEvent.widgetPreset.id,
      chartTypeId: this.widgetPreviewEvent.vizType.id,
      objectId: this.objectId,
      objectClassId: this.objectClassId,
      dataSourceObjectId,
      dataSourceObjectClassId,
    });
  }

  showWidgetPreviewPopup(
    widget: Widget | null,
    loading: boolean = false,
  ): void {
    // * store preview state
    this.previewWidget = widget;
    this.previewIsLoading = loading;

    // * reset to docked state when showing popup
    this.updateDockedState(true);

    // * reset drag position to origin
    this.dragPosition = { x: 0, y: 0 };

    // * compute dimensions only if widget is available and not loading
    if (widget && !loading) {
      this.currentPopupHeight = this.calculatePopupHeight(
        widget,
        this.currentPopupWidth,
      );
    }

    // * signal that preview should open (this will trigger popup positioning via combineLatest)
    this.showPreviewSubject.next(true);
  }

  closeWidgetPreviewPopup() {
    this.showPreviewSubject.next(false);
    this.previewWidget = null;
    this.previewIsLoading = false;
    this.updateDockedState(true);
    this.dragPosition = { x: 0, y: 0 };
    this.clearSelection();
  }

  dockWidgetPreviewPopup() {
    // * recalculate position based on current RTL/LTR settings
    this.language$
      .pipe(
        take(1),
        tap((language) => {
          this.calculatePopupPosition(language.rtl);

          // * reset drag offset to origin - this will move the popup back to the base position
          this.dragPosition = { x: 0, y: 0 };

          this.updateDockedState(true);
        }),
      )
      .subscribe();
  }

  onDragStarted() {}

  onDragEnded() {}

  onDragMoved(event: CdkDragMove) {
    if (this.isDocked) {
      this.updateDockedState(false);
    }
  }

  // checkbox toggle handler
  onPivotableToggle() {
    this.filterWidgets(this.searchString, this.pivotableOnly);
  }

  private clearSelection() {
    this.selectedPresetId = null;
    this.selectedChartTypeId = null;
  }

  // * the height is half of the width and some ratio
  // * Example sizeX: 10 and sizeY: 10 -> height is half the width
  // * Example sizeX: 10 and sizeY: 12 -> height is half the width * 1.2
  private calculatePopupHeight(widget: Widget, popupWidth: number) {
    return (popupWidth * (widget.sizeY / widget.sizeX)) / 2;
  }

  private calculatePopupPosition(rtl: boolean) {
    if (!this.sidebarRef?.nativeElement) {
      return;
    }

    const sidebarRect = this.sidebarRef.nativeElement.getBoundingClientRect();

    this.popupTop = sidebarRect.top;

    this.popupLeft = rtl
      ? sidebarRect.right + 20
      : sidebarRect.left - this.currentPopupWidth - 20;
  }

  trackByFn(index: number, item: WidgetPreset) {
    return item.id;
  }
}
